import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentService } from '../services/agentService';
import toast from 'react-hot-toast';

export const useMyAgentProfile = () =>
  useQuery({ queryKey: ['agent', 'profile'], queryFn: agentService.getMyProfile, retry: false });

export const useAgentSales = (params?: any) =>
  useQuery({ queryKey: ['agent', 'sales', params], queryFn: () => agentService.getSales(params) });

export const useAgentDashboard = (params?: any) =>
  useQuery({ queryKey: ['agent', 'dashboard', params], queryFn: () => agentService.getDashboard(params) });

export const useApplyForAgent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: agentService.applyForAgent,
    onSuccess: () => { toast.success('Application submitted!'); qc.invalidateQueries({ queryKey: ['agent'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to submit application'),
  });
};

// Admin hooks
export const useAdminAgents = (params?: any) =>
  useQuery({ queryKey: ['admin', 'agents', params], queryFn: () => agentService.adminGetAgents(params) });

export const useAdminAgent = (id: string) =>
  useQuery({ queryKey: ['admin', 'agents', id], queryFn: () => agentService.adminGetAgent(id), enabled: !!id });

export const useApproveAgent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentService.adminApproveAgent(id),
    onSuccess: () => { toast.success('Agent approved'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed'),
  });
};

export const useDisableAgent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) => agentService.adminDisableAgent(id, comment),
    onSuccess: () => { toast.success('Agent disabled'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed'),
  });
};

export const useRejectAgent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => agentService.adminRejectAgent(id, reason),
    onSuccess: () => { toast.success('Agent application rejected'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed'),
  });
};

export const useEnableAgent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentService.adminEnableAgent(id),
    onSuccess: () => { toast.success('Agent enabled'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed'),
  });
};

export const useAddAgentComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) => agentService.adminAddComment(id, comment),
    onSuccess: () => { toast.success('Comment added'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed'),
  });
};

export const useToggleLargeWin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => agentService.adminToggleLargeWin(id, enabled),
    onSuccess: () => { toast.success('Capability updated'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed'),
  });
};

export const useUpdateAgent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => agentService.adminUpdateAgent(id, data),
    onSuccess: () => { toast.success('Agent profile updated'); qc.invalidateQueries({ queryKey: ['admin', 'agents'] }); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update'),
  });
};
